import axios from "axios";
import { toast } from "sonner";
import { getSessionId } from "./planiq";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send the session cookie with every request (Emergent Google Auth).
axios.defaults.withCredentials = true;

// Gate Atlas / exports / real plans behind login: any 401 on a guarded action bounces
// the visitor to the landing page to sign in. The silent /auth/me + /auth/session checks
// opt out via `skipAuthRedirect`.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const cfg = err.config || {};
    if (err.response?.status === 401 && !cfg.skipAuthRedirect) {
      if (window.location.pathname !== "/") {
        toast.error("Please sign in to continue.");
        setTimeout(() => { window.location.href = "/"; }, 600);
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  // ---- auth ----
  me: () => axios.get(`${API}/auth/me`, { skipAuthRedirect: true }).then((r) => r.data),
  authSession: (session_id) =>
    axios.post(`${API}/auth/session`, { session_id }, { skipAuthRedirect: true }).then((r) => r.data),
  logout: () => axios.post(`${API}/auth/logout`).then((r) => r.data),

  // ---- admin ----
  adminListUsers: () => axios.get(`${API}/admin/users`).then((r) => r.data),
  adminBlockUser: (uid) => axios.post(`${API}/admin/users/${uid}/block`).then((r) => r.data),
  adminUnblockUser: (uid) => axios.post(`${API}/admin/users/${uid}/unblock`).then((r) => r.data),
  adminSetTier: (uid, tier) => axios.post(`${API}/admin/users/${uid}/tier`, { tier }).then((r) => r.data),
  adminDeleteUser: (uid) => axios.delete(`${API}/admin/users/${uid}`).then((r) => r.data),

  // ---- billing ----
  checkout: (package_id) =>
    axios.post(`${API}/payments/checkout`, { package_id, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (sessionId) =>
    axios.get(`${API}/payments/status/${sessionId}`, { skipAuthRedirect: true }).then((r) => r.data),
  paymentHistory: () => axios.get(`${API}/payments/history`).then((r) => r.data),

  // ---- plans ----
  createPlan: (title, raw_context, source_documents, plan_type) =>
    axios.post(`${API}/plans`, { session_id: getSessionId(), title, raw_context, source_documents, plan_type }).then((r) => r.data),
  createSample: () =>
    axios.post(`${API}/plans/sample`, { session_id: getSessionId() }).then((r) => r.data),
  parseDocuments: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return axios.post(`${API}/plans/parse-documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  getPlan: (id) => axios.get(`${API}/plans/${id}`).then((r) => r.data),
  listPlans: () => axios.get(`${API}/plans`).then((r) => r.data),
  savePlan: (id) => axios.patch(`${API}/plans/${id}`, { saved: true }).then((r) => r.data),
  renamePlan: (id, title) => axios.patch(`${API}/plans/${id}`, { title }).then((r) => r.data),
  deletePlan: (id) => axios.delete(`${API}/plans/${id}`).then((r) => r.data),
  generate: (id, pillars, constraints) =>
    axios.post(`${API}/plans/${id}/generate`, { pillars, constraints }).then((r) => r.data),
  getCards: (id) => axios.get(`${API}/plans/${id}/cards`).then((r) => r.data),
  patchCard: (cardId, patch) => axios.patch(`${API}/cards/${cardId}`, patch).then((r) => r.data),
  reorderCards: (id, horizon, ordered_ids) =>
    axios.post(`${API}/plans/${id}/cards/reorder`, { horizon, ordered_ids }).then((r) => r.data),
  synthesize: (id) => axios.post(`${API}/plans/${id}/synthesize`).then((r) => r.data),
  chat: (id, message, scope) => axios.post(`${API}/plans/${id}/chat`, { message, scope }).then((r) => r.data),
  chatJob: (id, jobId) => axios.get(`${API}/plans/${id}/chat/jobs/${jobId}`).then((r) => r.data),
  getChat: (id) => axios.get(`${API}/plans/${id}/chat`).then((r) => r.data),
  exportMarkdown: (id) => axios.get(`${API}/plans/${id}/export`, { params: { format: "markdown" } }).then((r) => r.data),
  exportPdfFile: (id, html, styles, notes = true) =>
    axios.post(`${API}/plans/${id}/export/pdf`, { html, styles, notes }).then((r) => r.data),
  exportDocxFile: (id) => axios.post(`${API}/plans/${id}/export/docx`).then((r) => r.data),
  listExports: (id) => axios.get(`${API}/plans/${id}/exports`).then((r) => r.data),
  elaborate: (id, title, kind = "pillar") =>
    axios.post(`${API}/plans/${id}/elaborate`, { title, kind }).then((r) => r.data),
};

export default api;
